import { Subscription } from "rxjs";
import { Router } from "@angular/router";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ImageEntity } from "../../../shared/entities/file/image.entity";
import {
  AdActionType,
  CampaignDatesType,
  CampaignLandingPageLinkType,
  CampaignPartnerStatus,
  CampaignType,
  CtaText,
  Gender,
  GoogleAdsBiddingStrategyName,
  ObjectiveType,
  PaymentType,
} from "../../../shared/enums/campaign.enums";
import { trackById } from "../../../shared/functions/track-by-Id";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import {
  BrandCampaignCalendarPost,
  BrandCampaignFacebookCalendarPost,
  BrandCampaignGoogleCalendarPost,
  BrandCampaignLinkedInCalendarPost,
} from "../../../shared/models/brandCampaignCalendarPost";
import {
  CampaignPostContentType,
  LinkedInPostContentType,
} from "../../../shared/enums/campaignPost.enums";
import { BrandCampaignDetails } from "../../../shared/models/brandCampaignDetail";
import { BrandUrl } from "../../brand.url";
import { BrandCampaignPartnersService } from "../shared/services/brand-campaign-partners.service";
import { BrandCampaignDialogService } from "../brand-campaign-dialog.service";
import { getDateFormatSignal } from "../../../shared/services/date.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";

enum CampaignSections {
  DetailsSettings,
  DetailsInfo,
  DetailsInvitePage,
  Content,
  Ads,
  AdsChannels,
  LandingPage,
  ContentCalendar,
  InvitePartners,
  Pos,
}

@Component({
  selector: "app-brand-campaign-summary",
  templateUrl: "./brand-campaign-summary.component.html",
  styleUrl: "./brand-campaign-summary.component.scss",
})
export class BrandCampaignSummaryComponent implements OnInit, OnDestroy {
  protected readonly campaign: BrandCampaign;
  protected readonly Gender = Gender;
  protected readonly ObjectiveType = ObjectiveType;
  protected readonly AdActionType = AdActionType;
  protected readonly PaymentType = PaymentType;
  protected readonly ContentType = CampaignPostContentType;
  protected readonly LinkedInContentType = LinkedInPostContentType;
  private readonly subscriptions = new Subscription();
  protected readonly CampaignSections = CampaignSections;
  protected INDIVIDUAL_LANDING_PAGE =
    CampaignLandingPageLinkType.IndividualLandingPage;
  protected RETAILER_OWN_LINK = CampaignLandingPageLinkType.RetailerOwnLink;
  protected ONE_SINGLE_LINK = CampaignLandingPageLinkType.OneSingleLink;
  protected CAMPAIGN_SAME_DATES_TYPE = CampaignDatesType.SameDates;
  protected campaignDetails: BrandCampaignDetails;
  protected loading = true;
  protected totalFacebookPosts = 0;
  protected totalGoogleAds = 0;
  protected totalLinkedInPosts = 0;
  protected totalMarketingMaterial = 0;
  protected totalPartnersInvited = 0;
  protected totalPartnersNotInvited = 0;
  protected invitedPartnerLoaded = false;
  protected dateFormat = getDateFormatSignal();
  protected brandCalendarPosts:
    | BrandCampaignFacebookCalendarPost[]
    | BrandCampaignGoogleCalendarPost[]
    | BrandCampaignLinkedInCalendarPost[];
  protected readonly trackBrandCampaignCalendarPostByFn =
    trackById<BrandCampaignCalendarPost>;
  protected readonly trackCalendarPostCarouselByFn = trackById<ImageEntity>;
  protected readonly BetaCampaignSections = [
    CampaignSections.DetailsSettings,
    CampaignSections.DetailsInfo,
    CampaignSections.DetailsInvitePage,
    CampaignSections.LandingPage,
    CampaignSections.InvitePartners,
  ];

  constructor(
    private readonly router: Router,
    private readonly brandCampaignDialogService: BrandCampaignDialogService,
    private readonly brandCampaignPartnersService: BrandCampaignPartnersService,
    brandCampaignService: BrandCampaignService,
  ) {
    this.campaign = brandCampaignService.currentBrandCampaign;
    this.campaignDetails = this.campaign.currentDetails;
    this.brandCalendarPosts = this.campaign.currentCalendar?.calendarPosts as
      | BrandCampaignFacebookCalendarPost[]
      | BrandCampaignGoogleCalendarPost[]
      | BrandCampaignLinkedInCalendarPost[];
  }

  public ngOnInit(): void {
    this.initData();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public initData(): void {
    this.totalFacebookPosts = 0;

    for (const category of this.campaign.currentDetails.categories) {
      for (const post of category.posts) {
        if (post.isFacebookPost) {
          this.totalFacebookPosts++;
        } else if (post.isGoogleAd) {
          this.totalGoogleAds++;
        } else if (post.isLinkedInPost) {
          this.totalLinkedInPosts++;
        }
      }

      this.totalMarketingMaterial += category.videos.length;
      this.totalMarketingMaterial += category.images.length;
      this.getBrandCampaignPartners();
    }
  }

  private getBrandCampaignPartners(): void {
    if (this.invitedPartnerLoaded) {
      return;
    }

    this.subscriptions.add(
      this.brandCampaignPartnersService
        .getInvitedPartners(this.campaign.id)
        .subscribe((partners) => {
          this.totalPartnersInvited = partners.filter(
            (partnerData) =>
              partnerData.status === CampaignPartnerStatus.WaitingToBeInvited,
          ).length;

          this.totalPartnersNotInvited =
            partners.length - this.totalPartnersInvited;

          this.invitedPartnerLoaded = true;
        }),
    );
  }

  protected previewPartnerCampaign(): void {
    this.brandCampaignDialogService.showDetailsPreview(this.campaign);
  }

  protected previewCampaignBox(): void {
    this.brandCampaignDialogService.showBoxPreview(this.campaign);
  }

  private getLinkUrl(link: CampaignSections): string {
    const brandId = this.campaign.brand.id;
    const campaignId = this.campaign.id;

    switch (link) {
      case CampaignSections.DetailsSettings:
        return BrandUrl.Campaign.DetailsSettings(brandId, campaignId);
      case CampaignSections.DetailsInfo:
        return BrandUrl.Campaign.DetailsInfo(brandId, campaignId);
      case CampaignSections.DetailsInvitePage:
        return BrandUrl.Campaign.DetailsInvitePage(brandId, campaignId);
      case CampaignSections.Content:
        return BrandUrl.Campaign.Content(brandId, campaignId);
      case CampaignSections.Ads:
        return BrandUrl.Campaign.Ads(brandId, campaignId);
      case CampaignSections.AdsChannels:
        return BrandUrl.Campaign.AdsChannels(brandId, campaignId);
      case CampaignSections.LandingPage:
        return BrandUrl.Campaign.LandingPage(brandId, campaignId);
      case CampaignSections.ContentCalendar:
        return BrandUrl.Campaign.ContentCalendar(brandId, campaignId);
      case CampaignSections.InvitePartners:
        return BrandUrl.Campaign.InvitePartners(brandId, campaignId);
      case CampaignSections.Pos:
        return BrandUrl.Campaign.Pos(brandId, campaignId);
      default:
        return "/";
    }
  }

  protected goToRoute(link: CampaignSections): void {
    this.router.navigateByUrl(this.getLinkUrl(link));
  }

  protected showPOS(): boolean {
    return this.campaign?.type !== CampaignType.DownloadOnly;
  }

  protected showLandingPage(): boolean {
    return this.campaign?.type !== CampaignType.DownloadOnly;
  }

  protected getCTAText(): string {
    return CtaText[this.campaign.postsCtaText as keyof typeof CtaText] ?? "";
  }

  protected getFacebookTargetingNames(): string {
    return this.campaign.facebookTargetingTypes.join(", ");
  }

  protected getGoogleTargeting(): string {
    return this.campaign.googleAdsAudienceNames.join(", ");
  }

  protected getGoogleBiddingStrategy(): string {
    return (
      GoogleAdsBiddingStrategyName[
        this.campaign.googleAdsBiddingStrategyType
      ] ?? ""
    );
  }

  protected getVideoUrl(post: BrandCampaignCalendarPost): string {
    const facebookPost = post as BrandCampaignFacebookCalendarPost;
    return facebookPost.videoUrl ?? "";
  }

  public isShown(section: CampaignSections): boolean {
    return !this.campaign.isBeta || this.BetaCampaignSections.includes(section);
  }

  protected getInvitationPreviewLink(): string {
    return (
      "https://" +
      window.location.hostname +
      "/invite/" +
      this.campaign.projectName +
      "/preview"
    );
  }
}
